import {
  Button,
  CommandDialog,
  CommandEmpty,
  CommandFooter,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  DialogClose,
  Kbd,
} from '@nexus/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { visibleSections } from '../admin/sections';
import { guideHref, searchGuides, useGuideTexts, useVisibleGuides } from '../guides/guides';
import { useDebounced } from '../lib/use-debounced';
import { useModules } from '../modules/context';
import type { NavModule } from '../modules/navigation';
import { groupHits, MIN_SEARCH_LENGTH, searchQuery, useHitOrigin } from '../search/api';
import { recentHits, rememberHit } from '../search/recent';
import { useSession } from '../session/session';
import { useUpdatePersonalSetting } from '../session/settings';
import { useGuardedSignOut } from './sign-out';

/** True on Apple devices, where the shortcut is ⌘K instead of Ctrl K. */
export function isApple(): boolean {
  return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

export function useSearchShortcut(onOpen: () => void): void {
  useEffect(() => {
    function handle(event: KeyboardEvent) {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey) && !event.altKey) {
        event.preventDefault();
        onOpen();
      }
    }
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [onOpen]);
}

/**
 * Ctrl/⌘ K: records of the modules and the kernel from the search service, plus pages, settings
 * and actions filtered in the browser (docs/architecture/search.md).
 */
export function SearchPalette({
  open,
  onOpenChange,
  navigation,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  navigation: readonly NavModule[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const update = useUpdatePersonalSetting();
  const [signOut, signOutDialog] = useGuardedSignOut();
  const session = useSession();
  const adminSections = visibleSections(session, useModules());
  const origin = useHitOrigin();
  const [query, setQuery] = useState('');
  const term = useDebounced(query.trim(), 200);
  const searching = term.length >= MIN_SEARCH_LENGTH;
  const results = useQuery({ ...searchQuery(term), enabled: open && searching });
  const hits = searching ? (results.data?.items ?? []).map((hit) => ({ hit, ...origin(hit) })) : [];
  const recent = open && !query ? recentHits(session.account.id) : [];
  const guides = useGuideTexts(useVisibleGuides(), open && searching);
  const guideHits = searching ? searchGuides(guides, term).slice(0, 5) : [];

  function change(next: boolean) {
    if (!next) setQuery('');
    onOpenChange(next);
  }

  function go(path: string) {
    change(false);
    void navigate({ href: path });
  }

  return (
    <>
      <CommandDialog open={open} onOpenChange={change} label={t('shell.search.label')}>
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder={t('shell.search.placeholder')}
          trailing={
            // Phones have no Escape key.
            <DialogClose asChild>
              <Button variant="ghost" size="sm" className="-mr-2 md:hidden">
                {t('ui.cancel')}
              </Button>
            </DialogClose>
          }
        />
        <CommandList>
          {results.isFetching && searching && <CommandLoading label={t('shell.search.loading')} />}
          <CommandEmpty>{t('shell.search.empty')}</CommandEmpty>
          {recent.length > 0 && (
            <CommandGroup heading={t('shell.search.recent')}>
              {recent.map((item) => (
                <CommandItem
                  key={item.path}
                  value={`recent ${item.title} ${item.path}`}
                  icon={item.icon}
                  onSelect={() => go(item.path)}
                >
                  {item.title}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {groupHits(hits).map((group) => (
            // Hits come filtered from the server; the browser filter must not hide them.
            <CommandGroup key={group[0]!.group} heading={group[0]!.label} forceMount>
              {group.map(({ hit, icon, accent }) => (
                <CommandItem
                  key={`${hit.entity}:${hit.id}`}
                  value={`hit ${hit.entity} ${hit.id}`}
                  forceMount
                  icon={icon}
                  accent={accent}
                  description={hit.subtitle ?? undefined}
                  onSelect={() => {
                    rememberHit(session.account.id, { title: hit.title, path: hit.path, icon });
                    go(hit.path);
                  }}
                >
                  {hit.title}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          {guideHits.length > 0 && (
            <CommandGroup heading={t('shell.search.guides')} forceMount>
              {guideHits.map((hit) => (
                <CommandItem
                  key={guideHref(hit)}
                  value={`guide ${guideHref(hit)}`}
                  forceMount
                  icon="book-open"
                  accent={hit.guide.accent}
                  description={hit.section?.title ?? hit.guide.summary}
                  onSelect={() => go(guideHref(hit))}
                >
                  {hit.guide.title}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {searching && hits.length > 0 && (
            <CommandGroup forceMount>
              <CommandItem
                value="search all"
                forceMount
                icon="search"
                onSelect={() => {
                  change(false);
                  void navigate({ to: '/search', search: { q: term } });
                }}
              >
                {t('shell.search.showAll', { term })}
              </CommandItem>
            </CommandGroup>
          )}
          <CommandGroup heading={t('shell.search.pages')}>
            <CommandItem icon="house" onSelect={() => go('/')}>
              {t('shell.home')}
            </CommandItem>
            {navigation.flatMap((module) =>
              module.items.map((item) => (
                <CommandItem
                  key={item.path}
                  value={`${t(module.label)} ${t(item.label)}`}
                  icon={item.icon}
                  accent={module.accent}
                  hint={t(module.label)}
                  onSelect={() => go(item.path)}
                >
                  {t(item.label)}
                </CommandItem>
              )),
            )}
          </CommandGroup>
          <CommandGroup heading={t('shell.search.settings')}>
            <CommandItem icon="user-cog" onSelect={() => go('/profile')}>
              {t('shell.profile.myProfile')}
            </CommandItem>
            <CommandItem icon="book-open" onSelect={() => go('/help')}>
              {t('shell.help')}
            </CommandItem>
          </CommandGroup>
          {adminSections.length > 0 && (
            <CommandGroup heading={t('shell.admin')}>
              {adminSections.map((section) => (
                <CommandItem
                  key={section.id}
                  value={`${t('shell.admin')} ${t(`admin.sections.${section.id}.label`)}`}
                  icon={section.icon}
                  hint={t(`admin.areas.${section.area}`)}
                  onSelect={() => go(section.path)}
                >
                  {t(`admin.sections.${section.id}.label`)}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandGroup heading={t('shell.search.actions')}>
            {(['light', 'dark', 'system'] as const).map((theme) => (
              <CommandItem
                key={theme}
                icon={theme === 'light' ? 'sun' : theme === 'dark' ? 'moon' : 'monitor'}
                hint={t('core.settings.theme.label')}
                onSelect={() => {
                  update.mutate({ key: 'core.theme', value: theme });
                  change(false);
                }}
              >
                {t(`core.settings.theme.options.${theme}`)}
              </CommandItem>
            ))}
            <CommandItem
              icon="log-out"
              onSelect={() => {
                change(false);
                signOut();
              }}
            >
              {t('common.signOut')}
            </CommandItem>
          </CommandGroup>
        </CommandList>
        <CommandFooter>
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            {t('shell.search.navigate')}
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>↵</Kbd>
            {t('shell.search.select')}
          </span>
          <span className="flex items-center gap-1.5">
            {/* Key caps read the same in every language. */}
            <Kbd>{'Esc'}</Kbd>
            {t('shell.search.dismiss')}
          </span>
        </CommandFooter>
      </CommandDialog>
      {signOutDialog}
    </>
  );
}
